import { injectable } from 'inversify';

import {
  EMPTY_CELL,
  MIN_ROWS_COUNT,
  OCCUPIED_CELL,
  WIDGET_CELL,
} from '../../../constants/dashboard';
import {
  DashboardGridItem,
  SectionDragState,
  SectionKey,
  Widget,
} from '../../../types';

@injectable()
export class DashboardGridService {
  /** Размещает виджет по индексу, помечая последующие ячейки как occupied */
  private placeWidgetAt(
    grid: DashboardGridItem[],
    widget: Widget,
    index: number,
  ): void {
    grid[index] = { ...WIDGET_CELL, widget };
    for (let i = 1; i < widget.size; i++) {
      grid[index + i] = OCCUPIED_CELL;
    }
  }

  /** Очищает ячейки виджета начиная с указанного индекса */
  private clearWidgetAt(
    grid: DashboardGridItem[],
    index: number,
    size: number,
  ): void {
    grid[index] = EMPTY_CELL;
    for (let i = 1; i < size; i++) {
      if (index + i < grid.length) {
        grid[index + i] = EMPTY_CELL;
      }
    }
  }

  /** Расширяет сетку пустыми строками по необходимой длине */
  private ensureGridCapacity(
    grid: DashboardGridItem[],
    length: number,
    cols: number,
  ): void {
    while (grid.length < length) {
      grid.push(...Array(cols).fill(EMPTY_CELL));
    }
  }

  /** Удаляет из сетки все полностью пустые строки */
  private removeEmptyRows(
    grid: DashboardGridItem[],
    cols: number,
  ): DashboardGridItem[] {
    const result: DashboardGridItem[] = [];
    for (let row = 0; row * cols < grid.length; row++) {
      const rowStart = row * cols;
      const rowItems = grid.slice(rowStart, rowStart + cols);
      const hasContent = rowItems.some((item) => item.type !== 'empty');
      if (hasContent) {
        result.push(...rowItems);
      }
    }
    return result;
  }

  /** Возвращает номер строки по индексу виджета */
  private getRowFromIndex(index: number, cols: number): number {
    return Math.floor(index / cols);
  }

  /** Возвращает глубину заполнения колонки — первую пустую строку после последнего занятого элемента */
  private getColumnDepth(
    col: number,
    grid: DashboardGridItem[],
    cols: number,
  ): number {
    let depth = 0;
    for (let row = 0; row * cols + col < grid.length; row++) {
      if (grid[row * cols + col].type !== 'empty') {
        depth = row + 1;
      }
    }
    return depth;
  }

  /** Вставляет пустую строку на указанную позицию, сдвигая содержимое ниже на одну строку вниз */
  private insertEmptyRowAt(
    grid: DashboardGridItem[],
    row: number,
    cols: number,
  ): DashboardGridItem[] {
    const result = [...grid];
    const insertAt = row * cols;
    result.splice(insertAt, 0, ...Array(cols).fill(EMPTY_CELL));
    return result;
  }

  /** Сдвигает стартовый индекс влево, чтобы виджет заданного размера не выходил за пределы строки */
  clampIndexInRow(
    targetIndex: number,
    cols: number,
    widgetSize: number,
  ): number {
    const targetRow = Math.floor(targetIndex / cols);
    const targetCol = targetIndex % cols;
    const maxStartCol = cols - widgetSize;
    const startCol = targetCol > maxStartCol ? maxStartCol : targetCol;
    return targetRow * cols + startCol;
  }

  /** Убирает пустые строки, затем сдвигает виджеты вверх в своих колонках */
  compactGrid(grid: DashboardGridItem[], cols: number): DashboardGridItem[] {
    let result = this.removeEmptyRows(grid, cols);

    const widgets: Widget[] = [];
    for (const item of result) {
      if (item.type === 'widget') {
        widgets.push(item.widget);
      }
    }

    widgets.sort((a, b) => {
      const rowA = Math.floor(this.findWidgetIndex(result, a.id) / cols);
      const rowB = Math.floor(this.findWidgetIndex(result, b.id) / cols);
      return rowA - rowB;
    });

    for (const widget of widgets) {
      const currentIndex = this.findWidgetIndex(result, widget.id);
      if (currentIndex === -1) continue;

      const startCol = currentIndex % cols;
      let currentRow = Math.floor(currentIndex / cols);

      while (currentRow > 0) {
        const tryIndex = (currentRow - 1) * cols + startCol;
        let canMove = true;
        for (let i = 0; i < widget.size; i++) {
          const cellIndex = tryIndex + i;
          if (
            cellIndex >= result.length ||
            result[cellIndex].type !== 'empty'
          ) {
            canMove = false;
            break;
          }
        }
        if (canMove) {
          currentRow--;
        } else {
          break;
        }
      }

      const newIndex = currentRow * cols + startCol;
      if (newIndex !== currentIndex) {
        this.clearWidgetAt(result, currentIndex, widget.size);
        this.placeWidgetAt(result, widget, newIndex);
      }
    }

    const compacted = this.removeEmptyRows(result, cols);
    compacted.push(...Array(cols).fill(EMPTY_CELL));

    return compacted.length > 0 ? compacted : Array(cols).fill(EMPTY_CELL);
  }

  /** Строит сетку по позициям виджетов, расширяя при необходимости, затем компактирует вертикально */
  buildGridFromPositions(widgets: Widget[], cols: number): DashboardGridItem[] {
    if (widgets.length === 0) {
      return Array(cols).fill(EMPTY_CELL);
    }

    let maxRow = 0;
    for (const widget of widgets) {
      const y = widget.y ?? 0;
      if (y + 1 > maxRow) maxRow = y + 1;
    }

    const gridLength = (maxRow + 1) * cols;
    const result: DashboardGridItem[] = Array(gridLength).fill(EMPTY_CELL);

    for (const widget of widgets) {
      const x = widget.x ?? 0;
      const y = widget.y ?? 0;
      const index = y * cols + x;

      this.ensureGridCapacity(result, index + widget.size, cols);
      this.placeWidgetAt(result, widget, index);
    }

    return this.compactGrid(result, cols);
  }

  /**
   * Располагает виджеты последовательно слева направо, сверху вниз,
   * перенося на новую строку, если виджет не помещается.
   */
  normalizeGrid(widgets: Widget[], cols: number): DashboardGridItem[] {
    if (widgets.length === 0) {
      return Array(cols).fill(EMPTY_CELL);
    }

    const result: DashboardGridItem[] = [];
    let colPosition = 0;

    for (const widget of widgets) {
      const spaceLeft = cols - colPosition;

      if (widget.size > spaceLeft) {
        result.push(...Array(spaceLeft).fill(EMPTY_CELL));
        colPosition = 0;
      }

      result.push({ ...WIDGET_CELL, widget });
      colPosition += 1;

      for (let i = 1; i < widget.size; i++) {
        result.push(OCCUPIED_CELL);
        colPosition += 1;
      }

      if (colPosition >= cols) {
        colPosition = 0;
      }
    }

    if (colPosition > 0) {
      result.push(...Array(cols - colPosition).fill(EMPTY_CELL));
    }

    result.push(...Array(cols).fill(EMPTY_CELL));

    return result;
  }

  /** Находит индекс виджета по id, или -1 если не найден */
  findWidgetIndex(grid: DashboardGridItem[], widgetId: string): number {
    return grid.findIndex(
      (item) => item.type === 'widget' && item.widget.id === widgetId,
    );
  }

  /** Извлекает все виджеты из сетки, сохраняя порядок */
  extractWidgets(grid: DashboardGridItem[]): Widget[] {
    return grid
      .filter((item) => item.type === 'widget')
      .map((item) => item.widget);
  }

  /** Возвращает массив id виджетов в сетке */
  getWidgetIds(grid: DashboardGridItem[]): string[] {
    return this.extractWidgets(grid).map((widget) => widget.id);
  }

  /**
   * Вставляет виджет в указанную ячейку, затем компактирует сетку
   * Позиции остальных виджетов сохраняются
   */
  insertWidgetAt(
    grid: DashboardGridItem[],
    widget: Widget,
    index: number,
    cols: number,
  ): DashboardGridItem[] {
    const result = [...grid];
    const adjustedIndex = this.clampIndexInRow(index, cols, widget.size);

    this.ensureGridCapacity(result, adjustedIndex + widget.size, cols);
    this.placeWidgetAt(result, widget, adjustedIndex);

    return this.compactGrid(result, cols);
  }

  /**
   * Удаляет виджет по индексу, заменяя его ячейки на пустые,
   * затем компактирует сетку
   */
  removeWidgetAt(
    grid: DashboardGridItem[],
    index: number,
    cols: number,
  ): DashboardGridItem[] {
    const item = grid[index];

    if (item?.type !== 'widget') {
      return grid;
    }

    const result = [...grid];
    this.clearWidgetAt(result, index, item.widget.size);

    return this.compactGrid(result, cols);
  }

  /** Возвращает копию сетки без указанного виджета */
  getGridWithoutWidget(
    grid: DashboardGridItem[],
    widgetId: string,
  ): DashboardGridItem[] {
    const result = [...grid];
    const index = this.findWidgetIndex(result, widgetId);

    if (index === -1) return result;

    const item = result[index];
    if (item.type !== 'widget') return result;
    this.clearWidgetAt(result, index, item.widget.size);

    return result;
  }

  /**
   * Вставляет виджет на позицию со сдвигом строки вниз,
   * освобождая место без вытеснения других виджетов
   */
  shiftAndInsertCard(
    grid: DashboardGridItem[],
    widget: Widget,
    targetIndex: number,
    cols: number,
  ): DashboardGridItem[] {
    const adjustedIndex = this.clampIndexInRow(targetIndex, cols, widget.size);
    const targetRow = Math.floor(adjustedIndex / cols);

    const result = this.insertEmptyRowAt(grid, targetRow, cols);
    this.placeWidgetAt(result, widget, adjustedIndex);

    return this.compactGrid(result, cols);
  }

  /** Проверяет, можно ли разместить виджет в указанной ячейке */
  private canDropWidgetAtCell(
    cellIndex: number,
    grid: DashboardGridItem[],
    cols: number,
    widgetSize: number,
  ): boolean {
    if (grid[cellIndex].type !== 'empty') {
      return false;
    }

    const targetRow = this.getRowFromIndex(cellIndex, cols);

    for (let i = 1; i < widgetSize; i++) {
      const nextIndex = cellIndex + i;
      const nextRow = this.getRowFromIndex(nextIndex, cols);

      if (
        nextRow !== targetRow ||
        nextIndex >= grid.length ||
        grid[nextIndex].type !== 'empty'
      ) {
        return false;
      }
    }

    return true;
  }

  /**
   * Ищет ближайшую допустимую позицию для drop виджета рядом с targetIndex
   * Сначала проверяет строку курсора, затем расширяет поиск вверх и вниз
   */
  findNearestDropPosition(
    targetIndex: number,
    grid: DashboardGridItem[],
    cols: number,
    widgetSize: number,
  ): number {
    const adjustedIndex = this.clampIndexInRow(targetIndex, cols, widgetSize);
    const startCol = adjustedIndex % cols;

    let maxDepth = 0;
    for (let c = startCol; c < startCol + widgetSize; c++) {
      maxDepth = Math.max(maxDepth, this.getColumnDepth(c, grid, cols));
    }

    const cursorRow = Math.min(Math.floor(adjustedIndex / cols), maxDepth);

    const tryRow = (row: number): number | -1 => {
      const cellIndex = row * cols + startCol;
      const adjustedCellIndex = this.clampIndexInRow(
        cellIndex,
        cols,
        widgetSize,
      );

      const checkGrid = [...grid];
      this.ensureGridCapacity(checkGrid, adjustedCellIndex + widgetSize, cols);

      if (
        this.canDropWidgetAtCell(adjustedCellIndex, checkGrid, cols, widgetSize)
      ) {
        return adjustedCellIndex;
      }
      return -1;
    };

    const cursorResult = tryRow(cursorRow);
    if (cursorResult !== -1) return cursorResult;

    for (let offset = 1; offset <= maxDepth; offset++) {
      if (cursorRow - offset >= 0) {
        const result = tryRow(cursorRow - offset);
        if (result !== -1) return result;
      }
      if (cursorRow + offset <= maxDepth) {
        const result = tryRow(cursorRow + offset);
        if (result !== -1) return result;
      }
    }

    return this.clampIndexInRow(maxDepth * cols + startCol, cols, widgetSize);
  }

  /** Пересчитывает координаты у всех виджетов по их позиции в сетке */
  updateWidgetPositions(grid: DashboardGridItem[], cols: number): Widget[] {
    const result: Widget[] = [];
    for (let i = 0; i < grid.length; i++) {
      const item = grid[i];
      if (item.type !== 'widget') continue;
      result.push({ ...item.widget, x: i % cols, y: Math.floor(i / cols) });
    }
    return result;
  }

  /** Строит сетку секции с гарантией минимального количества строк */
  private buildSectionGrid(
    widgets: Widget[],
    cols: number,
    sectionKey: SectionKey,
  ): DashboardGridItem[] {
    let grid =
      widgets.length > 0
        ? this.buildGridFromPositions(widgets, cols)
        : Array(cols).fill(EMPTY_CELL);

    const minCells = MIN_ROWS_COUNT[sectionKey] * cols;
    while (grid.length < minCells) {
      grid.push(...Array(cols).fill(EMPTY_CELL));
    }

    const totalRows = Math.ceil(grid.length / cols);
    const lastRowStart = (totalRows - 1) * cols;
    const lastRowHasWidget = grid
      .slice(lastRowStart)
      .some((item) => item.type !== 'empty');
    if (lastRowHasWidget) {
      grid.push(...Array(cols).fill(EMPTY_CELL));
    }

    return grid;
  }

  /** Строит отображаемую сетку секции с учётом перетаскивания */
  buildDisplayGrid(
    widgets: Widget[],
    cols: number,
    sectionKey: SectionKey,
    dragState: SectionDragState,
  ): DashboardGridItem[] {
    const { activeItem, isDraggingItem, isDraggingFromAvailable } = dragState;
    const grid = this.buildSectionGrid(widgets, cols, sectionKey);

    if (!isDraggingFromAvailable && isDraggingItem && activeItem) {
      let compacted = this.compactGrid(
        this.getGridWithoutWidget(grid, activeItem.id || ''),
        cols,
      );

      const targetCells = grid.length;
      while (compacted.length < targetCells) {
        compacted.push(...Array(cols).fill(EMPTY_CELL));
      }

      return compacted.map((item, index) => {
        if (item.type === 'widget') {
          return {
            type: 'widget' as const,
            widget: {
              ...item.widget,
              x: index % cols,
              y: Math.floor(index / cols),
            },
          };
        }
        return item;
      });
    }

    return grid;
  }

  /** Вычисляет визуальную позицию ячейки с учётом сдвига и коллапса */
  computeCellPosition(
    index: number,
    cols: number,
    shiftTargetRow: number,
    collapsedFromIndex: number,
  ): {
    visualRow: number;
    col: number;
    isCollapsed: boolean;
    effectiveRow: number;
  } {
    const tempRowCount = 2;
    const baseRow = Math.floor(index / cols);
    const col = (index % cols) + 1;
    const shouldShift = shiftTargetRow !== -1 && baseRow >= shiftTargetRow;
    const visualRow = shouldShift ? baseRow + tempRowCount : baseRow + 1;
    const isCollapsed = collapsedFromIndex >= 0 && index >= collapsedFromIndex;
    const effectiveRow = isCollapsed ? visualRow - 1 : visualRow;

    return { visualRow, col, isCollapsed, effectiveRow };
  }
}
